import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { trackBeginCheckout } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ShoppingBag, Trash2, Loader2, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, removeItem, clear, subtotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [discount, setDiscount] = useState("");
  const [applied, setApplied] = useState(null);
  const [consent, setConsent] = useState(false);

  const hasCustom = items.some((i) => !i.is_instant_download);
  const total = Math.max(subtotal - (applied?.amount || 0), 0);

  const applyDiscount = async () => {
    try {
      const { data } = await api.post("/discounts/validate", {
        code: discount, subtotal, product_slugs: items.map((i) => i.product_slug), user_id: user?.id || null,
      });
      if (data.valid) { setApplied(data); toast.success(data.message); }
      else { setApplied(null); toast.error(data.message); }
    } catch (err) { toast.error(apiError(err)); }
  };

  const checkout = async () => {
    if (!user) { navigate("/login", { state: { from: "/cart" } }); return; }
    if (hasCustom && !consent) { toast.error("Please confirm the photo usage consent"); return; }
    setBusy(true);
    trackBeginCheckout({ value: total, items: items.map((i) => ({ slug: i.product_slug, name: i.product_name, price: i.unit_price, quantity: i.quantity })) });
    try {
      const payload = {
        items: items.map((i) => ({ product_slug: i.product_slug, variant_id: i.variant_id, upsell_keys: i.upsell_keys || [], quantity: i.quantity })),
        personalization: { customer_name: user.name, email: user.email },
        discount_code: applied?.code || null,
        referral_code: localStorage.getItem("mfm_ref") || null,
        photo_consent: true,
        marketing_consent: false,
      };
      const { data: order } = await api.post("/orders", payload);
      const { data } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
      clear();
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto" />
        <h1 className="font-display text-2xl font-bold text-slateink mt-4">Your cart is empty</h1>
        <p className="text-slate-500 mt-2">Discover keepsakes made for the people you love.</p>
        <Button asChild className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold mt-6"><Link to="/shop">Browse products</Link></Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl font-extrabold text-slateink mb-8">Your cart</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((i) => (
            <div key={i._key} data-testid="cart-item" className="flex gap-4 bg-white rounded-2xl border border-slate-100 p-4">
              <img src={i.image} alt={i.product_name} className="w-20 h-20 rounded-xl object-cover" />
              <div className="flex-1">
                <p className="font-semibold text-slateink">{i.product_name}</p>
                <p className="text-sm text-slate-500">{i.variant_label}</p>
                {i.upsell_keys?.length > 0 && <p className="text-xs text-slate-400 mt-0.5">{i.upsell_keys.length} extra(s)</p>}
                <p className="font-semibold text-slateink mt-1">${i.unit_price.toFixed(2)}</p>
              </div>
              <button onClick={() => removeItem(i._key)} data-testid="cart-remove" className="text-slate-400 hover:text-rose self-start p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <div>
          <div className="bg-white rounded-2xl border border-slate-100 p-6 lg:sticky lg:top-24">
            <h3 className="font-semibold text-slateink mb-4">Order summary</h3>
            <div className="flex gap-2 mb-4">
              <Input value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="Discount code" data-testid="cart-discount" className="uppercase" />
              <Button variant="outline" onClick={applyDiscount} data-testid="cart-apply-discount">Apply</Button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              {applied && <div className="flex justify-between text-sage"><span>Discount ({applied.code})</span><span>−${applied.amount.toFixed(2)}</span></div>}
              <div className="flex justify-between font-semibold text-lg pt-2 border-t border-slate-100"><span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span></div>
            </div>
            {hasCustom && (
              <label className="flex items-start gap-2 mt-4 text-xs text-slate-600 cursor-pointer">
                <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="cart-consent" className="mt-0.5" />
                <span>I understand my uploaded images will only be used to create my purchased product.</span>
              </label>
            )}
            <Button onClick={checkout} disabled={busy} data-testid="cart-checkout" className="w-full rounded-full bg-amber hover:bg-amber-600 text-white font-semibold mt-5 h-11">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-1" /> Checkout</>}
            </Button>
            {hasCustom && <p className="text-xs text-slate-400 mt-3 text-center">You'll add photos &amp; details after checkout from your account.</p>}
            <Link to="/shop" className="block text-center text-sm text-amber font-semibold mt-3">Continue shopping <ArrowRight className="w-3 h-3 inline" /></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
